import { Trans, useLingui } from "@lingui/react/macro";
import { useMemo } from "react";

import type { DataTableInstance } from "../../table-features";
import type { MultiFilterField } from "../types";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../../base/command";
import { BaseCheckbox } from "../../../../fields/base-checkbox";
import { resolveEnumLabel } from "../../../../fields/utils";
import { sameFilterValue } from "../../filter-fns";
import {
  getMultiSelected,
  resolveOptions,
  toggleMulti,
} from "../tanstack-bridge";

export function MultiEditor<T, V>({
  table,
  field,
  showSearch = false,
}: {
  table: DataTableInstance<T>;
  field: MultiFilterField<T, V>;
  showSearch?: boolean;
}) {
  const { t } = useLingui();
  const data = table.getCoreRowModel().rows;
  // `data` (the rows array) is stable across renders that don't change the
  // underlying data, so the resolver only re-runs when rows actually change.
  const options = useMemo(
    () => resolveOptions(field, table),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `table` is a stable instance; `data` (the rows it resolves options from) is the actual reactive input
    [field, data],
  );
  const selected = getMultiSelected(table, field);
  const selectedKey = selected.join("|");
  const sortedEntries = useMemo(() => {
    const entries = Array.from(options.entries()).map(
      ([value, label]) => [value, resolveEnumLabel(label)] as [V, string],
    );
    const isSelected = (value: V) =>
      selected.some((v) => sameFilterValue(v, value));
    // A selected value with no matching option (e.g. an auto-applied filter for
    // a value absent from a row-derived option list) would otherwise render
    // nowhere — invisible and only removable via "Clear". Synthesize an entry so
    // every active selection shows as checked and can be unchecked; `String` is
    // the same label `getCountryOptions`/`uniqueValueOptions` use for real ones.
    const missing = selected
      .filter((value) => !entries.some(([v]) => sameFilterValue(v, value)))
      .map((value) => [value, String(value)] as [V, string]);
    const all = [...entries, ...missing];
    return [
      ...all.filter(([value]) => isSelected(value)),
      ...all.filter(([value]) => !isSelected(value)),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedKey captures the contents of `selected` (which is a fresh slice every read)
  }, [options, selectedKey]);
  return (
    <Command className="rounded-none bg-transparent">
      {showSearch && (
        <CommandInput
          placeholder={t`Search…`}
          wrapperClassName="border-input mx-1.5 mb-1 rounded-lg border"
        />
      )}
      <CommandList className="max-h-60 py-1">
        <CommandEmpty>
          <Trans>No results.</Trans>
        </CommandEmpty>
        <CommandGroup className="p-0">
          {sortedEntries.map(([value, label]) => {
            const checked = selected.some((v) => sameFilterValue(v, value));
            return (
              <CommandItem
                key={String(value)}
                value={label}
                onSelect={() => toggleMulti(table, field, value)}
                className="gap-2.5 px-3 py-1.5 rounded-md text-sm bg-transparent data-[selected=true]:bg-transparent hover:data-[selected=true]:bg-muted cursor-pointer"
              >
                <BaseCheckbox
                  checked={checked}
                  tabIndex={-1}
                  className="pointer-events-none"
                />
                {field.renderOption?.(value, label) ?? label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
